import { IntegrationFlowKind } from "client/Api/IntegrationBuilder/integrationBuilderFlows";
import {
	Dialog,
	FontIconName,
	Toolbar
} from "client/Components";
import { generateTrackingId, isNil } from "client/Misc/utils";
import { localize } from "client_legacy/ndaw/culture";
import {
	ReactElement,
	useCallback,
	useMemo,
	useState
} from "react";
import styles from "./TypeSelectionDialog.module.less";
import { useDataInputDialog } from "client/Hooks";
import { integrationFlowKindToLocalizationKey } from "./common";
import { never } from "client_legacy/ndaw/errors";
import { SelectionCard } from "./SelectionCard";

export type TypeSelectionDialogProps = {
	creatableKinds: readonly IntegrationFlowKind[];
	onSubmit: (kind: IntegrationFlowKind) => void;
	onClose: () => void;
};

export const TypeSelectionDialog = (props: TypeSelectionDialogProps): ReactElement => {
	const {
		creatableKinds,
		onSubmit,
		onClose
	} = props;

	const [kind, setKind] = useState<IntegrationFlowKind | null>(null);

	const submitHandler = useCallback(
		(): void => {
			if (isNil(kind)) {
				throw new Error("No kind selected");
			}

			onSubmit(kind);
		},
		[kind, onSubmit]
	);

	const cardsToRender = useMemo(
		(): readonly CardData[] => {
			return creatableKinds.map(getCardForKind);
		},
		[creatableKinds]
	);

	return (
		<Dialog
			trackingId="integration-builder-type-selection-dialog"
			onClose={onClose}
		>
			<Dialog.Header
				title={localize("_SelectIntegrationType")}
				onClose={onClose}
			/>
			<Dialog.Body>
				<ul className={styles.list}>
					{cardsToRender.map(
						(card): ReactElement => (
							<li
								key={card.kind}
								className={styles.item}
							>
								<SelectionCard
									icon={card.icon}
									title={localize(card.title)}
									description={localize(card.description)}
									isSelected={kind === card.kind}
									onClick={() => setKind(card.kind)}
									onDoubleClick={() => onSubmit(card.kind)}
									trackingId={generateTrackingId("integration-type", `|${card.kind}`)}
								/>
							</li>
						)
					)}
				</ul>
			</Dialog.Body>
			<Dialog.Footer>
				<Toolbar>
					<Toolbar.Button
						trackingId="choose-type"
						text={localize("_ChooseType")}
						kind="primary"
						onClick={submitHandler}
						isDisabled={isNil(kind)}
					/>
				</Toolbar>
			</Dialog.Footer>
		</Dialog>
	);
};
TypeSelectionDialog.displayName = "TypeSelectionDialog";


type CardData = {
	kind: IntegrationFlowKind;
	icon: FontIconName;
	title: LocalizationKey;
	description: LocalizationKey;
};

function getCardForKind(kind: IntegrationFlowKind): CardData {
	switch (kind) {
		case IntegrationFlowKind.App:
			return {
				kind: IntegrationFlowKind.App,
				icon: "grid-2",
				title: integrationFlowKindToLocalizationKey[IntegrationFlowKind.App],
				description: "_CreateAnIntegrationThatDisplaysLiveInformationDetails",
			};
		case IntegrationFlowKind.GlobalSearch:
			return {
				kind: IntegrationFlowKind.GlobalSearch,
				icon: "magnifying-glass",
				title: integrationFlowKindToLocalizationKey[IntegrationFlowKind.GlobalSearch],
				description: "_CreateAnIntegrationThatBringsSearchResultsDetails"
			};
		case IntegrationFlowKind.Ai:
			return {
				kind: IntegrationFlowKind.Ai,
				icon: "sparkles",
				title: integrationFlowKindToLocalizationKey[IntegrationFlowKind.Ai],
				description: "_CreateAnIntegrationThatAllowsTheAIAssistantDetails"
			};
		default:
			never(kind);
	}
}

type TypeSelectionDialogInitialData = Omit<TypeSelectionDialogProps, "onClose" | "onSubmit">;

export function useTypeSelectionDialog(): [
	element: ReactElement | null,
	open: (props: TypeSelectionDialogInitialData) => Promise<TryResult<IntegrationFlowKind>>
] {
	return useDataInputDialog(
		(props, onClose, onSubmit) => (
			<TypeSelectionDialog
				{...props}
				onClose={onClose}
				onSubmit={onSubmit}
			/>
		)
	);
}